import { z } from 'zod';
import {
  countryCodeSchema,
  currencyCodeSchema,
  emailSchema,
  longTextSchema,
  phoneSchema,
  shortTextSchema,
  timezoneSchema,
  uuidSchema,
} from '../primitives.js';
import {
  BILLING_INTERVALS,
  BUSINESS_TYPES,
  SELECTABLE_BUSINESS_TYPES,
  MEMBER_ROLES,
  MEMBER_STATUSES,
  OFFERING_KINDS,
  SHOP_PRESENCES,
  STOCK_POLICIES,
} from '../enums.js';
import { PERMISSIONS } from '../permissions.js';

/**
 * Onboarding. Timezone is optional: when omitted the API derives it from the
 * selected country, which is right often enough that asking would be friction.
 */
export const createBusinessSchema = z.object({
  name: shortTextSchema,
  /**
   * A kind of shop, and `platform` is not one.
   *
   * This is the CenoX Business signup: it says what kind of business this is,
   * and every answer furnishes the workspace accordingly. `platform` is what
   * the system writes for a workspace created WITHOUT Business — never
   * something a person submits here.
   */
  type: z.enum(SELECTABLE_BUSINESS_TYPES),
  countryCode: countryCodeSchema,
  /**
   * The state or region within the country, optional.
   *
   * The same code list `updateBusinessSchema` takes, and checked against the
   * chosen country by the API: a remittance default is priced against it.
   */
  stateCode: z.string().trim().min(2).max(8).nullable().optional(),
  currencyCode: currencyCodeSchema,
  timezone: timezoneSchema.optional(),
  phone: phoneSchema.optional(),
  email: emailSchema.optional(),
  address: longTextSchema.optional(),

  /** What the business sells. Pre-selected from its type's template. */
  offeringKind: z.enum(OFFERING_KINDS).default('both'),
  /** Where it sells. Decides which channels start switched on. */
  shopPresence: z.enum(SHOP_PRESENCES).default('physical'),
  /**
   * Which template to seed from. A preset (`clothing_fashion`, `pharmacy`, …)
   * refines the persisted `type` without needing its own enum value — see
   * templates.ts for why that separation exists.
   */
  presetSlug: z.string().trim().max(40).nullable().optional(),
  /** Name for the first branch. Defaults to "Main". */
  mainLocationName: shortTextSchema.optional(),
  /** Uploaded ahead of the business, then attached on creation. */
  logoAttachmentId: uuidSchema.nullable().optional(),
});
export type CreateBusinessInput = z.infer<typeof createBusinessSchema>;

export const updateBusinessSchema = z.object({
  name: shortTextSchema.optional(),
  /**
   * Accepted only to be refused: a business's type is fixed when it is
   * created (409 business_type_locked). Sending the type it already has is
   * not a change.
   */
  type: z.enum(SELECTABLE_BUSINESS_TYPES).optional(),
  countryCode: countryCodeSchema.optional(),
  /**
   * The state or region within the country.
   *
   * A code from `STATES_BY_COUNTRY`, not free text: a default remittance is
   * priced against it, and two spellings of Enugu would be two prices.
   */
  stateCode: z.string().trim().min(2).max(8).nullable().optional(),
  currencyCode: currencyCodeSchema.optional(),
  timezone: timezoneSchema.optional(),
  phone: phoneSchema.nullable().optional(),
  email: emailSchema.nullable().optional(),
  address: longTextSchema.nullable().optional(),
  offeringKind: z.enum(OFFERING_KINDS).optional(),
  shopPresence: z.enum(SHOP_PRESENCES).optional(),
  stockPolicy: z.enum(STOCK_POLICIES).optional(),
  logoAttachmentId: uuidSchema.nullable().optional(),
});
export type UpdateBusinessInput = z.infer<typeof updateBusinessSchema>;

export const businessSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  stateCode: z.string().nullable().optional(),
  type: z.enum(BUSINESS_TYPES),
  countryCode: z.string(),
  currencyCode: z.string(),
  timezone: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  offeringKind: z.enum(OFFERING_KINDS),
  shopPresence: z.enum(SHOP_PRESENCES),
  stockPolicy: z.enum(STOCK_POLICIES),
  presetSlug: z.string().nullable(),
  logoAttachmentId: uuidSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Business = z.infer<typeof businessSchema>;

// ── Members ─────────────────────────────────────────────────────────────────

const permissionOverridesSchema = z.object({
  grant: z.array(z.enum(PERMISSIONS)).optional(),
  revoke: z.array(z.enum(PERMISSIONS)).optional(),
});

export const inviteMemberSchema = z.object({
  email: emailSchema,
  role: z.enum(MEMBER_ROLES),
  fullName: shortTextSchema.optional(),
});
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>;

export const updateMemberSchema = z.object({
  role: z.enum(MEMBER_ROLES).optional(),
  status: z.enum(MEMBER_STATUSES).optional(),
  permissions: permissionOverridesSchema.nullable().optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberSchema>;

/**
 * Handing the business to an existing member.
 *
 * Typed confirmation, as with deleting things: TRANSFER or the business name.
 * The current owner becomes an Admin; the target becomes the one owner.
 */
export const transferOwnershipSchema = z.object({
  confirmation: z.string().trim().min(1).max(200),
});
export type TransferOwnershipInput = z.infer<typeof transferOwnershipSchema>;

export const memberSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  userId: uuidSchema.nullable(),
  email: z.string(),
  fullName: z.string().nullable(),
  role: z.enum(MEMBER_ROLES),
  status: z.enum(MEMBER_STATUSES),
  permissions: permissionOverridesSchema.nullable(),
  joinedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Member = z.infer<typeof memberSchema>;

// ── Branches ────────────────────────────────────────────────────────────────
// Present in Phase 1 so every financial record can carry a branch_id from day
// one. Full branch management UI arrives with the industry modules.
//
// A branch is where a business stops being single-country. The three locality
// fields are all optional and all null by default: null means "inherit the
// business", which is what a single-location business always means and what
// every branch created before this existed still means.

/**
 * What kind of place this is.
 *
 * `physical` is a shop with a door. `external_mobile` is a sales point that
 * takes stock out and brings money back — a market team, a van, a hawker —
 * and it is the distinction consignment tracking is built on, because
 * "issued out and not yet remitted" is a question only the second kind
 * raises. Called a LOCATION everywhere a user can see; `branches` is the
 * table it has always lived in.
 */
export const LOCATION_TYPES = ['physical', 'external_mobile'] as const;
export type LocationType = (typeof LOCATION_TYPES)[number];

export const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  physical: 'Physical branch',
  external_mobile: 'External / mobile',
};

export const createBranchSchema = z.object({
  name: shortTextSchema,
  type: z.enum(LOCATION_TYPES).default('physical'),
  // Null clears, undefined leaves alone — an owner who empties the address
  // field on an existing branch means the first, not the second.
  code: z.string().trim().max(24).nullable().optional(),
  address: longTextSchema.nullable().optional(),
  phone: phoneSchema.nullable().optional(),
  /** Where this branch operates. Omit to inherit the business's country. */
  countryCode: countryCodeSchema.nullable().optional(),
  /**
   * The state inside that country, overriding the business's. A code from
   * `STATES_BY_COUNTRY`, never free text — the same rule the business's own
   * state follows, because remittance is priced against it. Null inherits.
   */
  stateCode: z.string().trim().min(2).max(8).nullable().optional(),
  /** The branch's local time. Omit to inherit the business's. */
  timezone: timezoneSchema.nullable().optional(),
  /** What this branch trades in. Omit to inherit the business's currency. */
  currencyCode: currencyCodeSchema.nullable().optional(),
  isDefault: z.boolean().optional(),
});
export type CreateBranchInput = z.infer<typeof createBranchSchema>;

export const updateBranchSchema = createBranchSchema.partial().extend({
  isActive: z.boolean().optional(),
  /**
   * Move this location's delivery entry to one system or the other.
   *
   * Set to null to undo the decision and accept both again — a changeover that
   * has to be paused is a changeover somebody needs to be able to pause.
   */
  deliveryEntrySystem: z.enum(['dispatch', 'cenox']).nullable().optional(),
});
export type UpdateBranchInput = z.infer<typeof updateBranchSchema>;

export const branchSchema = z.object({
  /**
   * Where NEW deliveries for this location are entered during a changeover.
   *
   * `null` — undecided, both systems accepted (today's behaviour everywhere).
   * `'dispatch'` — the CenoHub Shop Entry owns it; CenoX refuses new entries.
   * `'cenox'` — CenoX owns it; the Shop Entry refuses new entries.
   *
   * Never affects an existing delivery: a job booked before the switch still
   * takes its updates, cancellations and corrections from wherever it was made.
   */
  deliveryEntrySystem: z.enum(['dispatch', 'cenox']).nullable().optional(),
  deliveryCutoverAt: z.string().nullable().optional(),
  id: uuidSchema,
  businessId: uuidSchema,
  name: z.string(),
  type: z.enum(LOCATION_TYPES),
  code: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  /** As stored. Null means "inherits" — see `effective*` below for the answer. */
  countryCode: z.string().nullable(),
  /** As stored: this location's own state. Null inherits the business's. */
  stateCode: z.string().nullable().optional(),
  timezone: z.string().nullable(),
  currencyCode: z.string().nullable(),
  /**
   * The stored value or the business's, resolved.
   *
   * Both shapes are returned because they answer different questions: the UI
   * needs to know whether a branch has been given its own country (so it can
   * show "inherited" rather than a value the owner never chose), while
   * anything doing work with the branch needs the resolved answer.
   */
  effectiveCountryCode: z.string(),
  effectiveTimezone: z.string(),
  effectiveCurrencyCode: z.string(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type Branch = z.infer<typeof branchSchema>;

/**
 * Where a business operates, derived from its branches rather than stored.
 *
 * There is deliberately no `business_countries` table: a stored list and the
 * branches it claims to describe drift the moment someone opens a location
 * without updating it. The branches ARE the facts.
 */
export const operatingCountriesSchema = z.object({
  /** Country of registration — the business's home. */
  registeredCountryCode: z.string(),
  countries: z.array(
    z.object({
      countryCode: z.string(),
      branchCount: z.number(),
      /** True for the country the business is registered in. */
      isRegistered: z.boolean(),
      currencyCodes: z.array(z.string()),
    }),
  ),
  isMultiCountry: z.boolean(),
});
export type OperatingCountries = z.infer<typeof operatingCountriesSchema>;

// ── Commercial layer ────────────────────────────────────────────────────────

/**
 * Asking to move to a plan.
 *
 * Deliberately named `checkout` rather than `changePlan`: it opens an invoice
 * and grants nothing. The old `changePlanSchema` is gone rather than
 * deprecated, because a schema that still exists is a schema somebody wires a
 * route to.
 */
export const checkoutSchema = z.object({
  /** The plan's stable key, never its display name. */
  planKey: z.string().trim().min(1).max(40),
  interval: z.enum(BILLING_INTERVALS).default('monthly'),
  /** Defaults to the subscription's current currency. */
  currencyCode: currencyCodeSchema.optional(),
  /**
   * The customer has seen what this plan takes away — modules in use, users or
   * locations past its limits — and wants it anyway. Without it, such a
   * change is refused with the list, never silently made.
   */
  acknowledgeChanges: z.boolean().optional(),
});
export type CheckoutInput = z.infer<typeof checkoutSchema>;

/**
 * Buying extra transaction capacity for the current period.
 *
 * `acknowledge` is not ceremony. A plan card shows its price before the button
 * is pressed; this purchase is a number typed into a field, so the price is
 * quoted back and the purchase refused until the customer confirms it. That is
 * what "explicit customer approval" has to mean for something bought by
 * quantity.
 */
export const extraTransactionsSchema = z.object({
  /** Packages of `overage_unit_size` transactions — 500, at the time of writing. */
  packages: z.number().int().min(1).max(100),
  acknowledge: z.boolean(),
});
export type ExtraTransactionsInput = z.infer<typeof extraTransactionsSchema>;

export const cancelSubscriptionSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});
export type CancelSubscriptionInput = z.infer<typeof cancelSubscriptionSchema>;

export const settleInvoiceSchema = z.object({
  /** Kept on the invoice: "who marked this paid" is the first question asked. */
  note: z.string().trim().min(3).max(500),
  /**
   * Where the money is: the bank transfer's reference, the receipt number.
   * Unique per provider, so one transfer cannot settle two invoices.
   */
  paymentReference: z.string().trim().min(3).max(120),
  paymentMethod: z.enum(['bank_transfer', 'cash', 'card', 'other']),
});
export type SettleInvoiceInput = z.infer<typeof settleInvoiceSchema>;

export const setModuleSchema = z.object({ enabled: z.boolean() });
export type SetModuleInput = z.infer<typeof setModuleSchema>;

export const putIntegrationSecretSchema = z.object({
  provider: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(60),
  /** Write-only. No read path returns this, by design. */
  value: z.string().min(1).max(4096),
});
export type PutIntegrationSecretInput = z.infer<typeof putIntegrationSecretSchema>;

/**
 * Erasure asks for the business's own name typed out, not a checkbox.
 * A confirm dialog is muscle memory; a name is not.
 */
export const eraseTenantSchema = z.object({
  confirmation: z.string().min(1),
  reason: z.string().trim().min(3).max(500),
  acknowledgeNoExport: z.boolean().optional(),
});
export type EraseTenantInput = z.infer<typeof eraseTenantSchema>;

// ── Staff ↔ locations ───────────────────────────────────────────────────────
//
// Where a person works. Deliberately NOT what their login may read — that is
// access control, it belongs on the member rather than the staff record, and
// conflating the two would make "Chidi covers the Annex on Saturdays" silently
// grant him the Annex's books.

export const assignStaffLocationsSchema = z.object({
  /** Every location this person works at. Replaces the current set. */
  branchIds: z.array(uuidSchema).max(100),
  /** Their home location. Must be one of `branchIds`, or null. */
  primaryBranchId: uuidSchema.nullable().optional(),
});
export type AssignStaffLocationsInput = z.infer<typeof assignStaffLocationsSchema>;

export const staffLocationSchema = z.object({
  branchId: uuidSchema,
  branchName: z.string(),
  type: z.enum(LOCATION_TYPES),
  isPrimary: z.boolean(),
  isActive: z.boolean(),
});
export type StaffLocation = z.infer<typeof staffLocationSchema>;

// ── Member ↔ location access ────────────────────────────────────────────────
//
// This is ACCESS, not rota. `staff_locations` records where a person works;
// this records what a LOGIN may read and write. They are deliberately separate
// tables against separate entities, because a staff record is not a login: a
// business has staff who never sign in, and members who are never on the shop
// floor.

export const LOCATION_SCOPES = ['all', 'assigned'] as const;
export type LocationScope = (typeof LOCATION_SCOPES)[number];

export const LOCATION_SCOPE_LABELS: Record<LocationScope, string> = {
  all: 'Every location',
  assigned: 'Only assigned locations',
};

export const setMemberLocationsSchema = z
  .object({
    /**
     * `all` means business-wide, and the assignment list is then irrelevant.
     *
     * Stated explicitly rather than inferred from an empty list: inferring
     * "all" from "none assigned" would mean removing somebody's last location
     * silently granted them everything — failing open in the one place where
     * that is unacceptable.
     */
    scope: z.enum(LOCATION_SCOPES),
    /** The locations this member may touch, when scope is `assigned`. */
    branchIds: z.array(uuidSchema).max(200).default([]),
  })
  .refine((body) => body.scope === 'all' || body.branchIds !== undefined, {
    message: 'List the locations this person may see',
    path: ['branchIds'],
  });
export type SetMemberLocationsInput = z.infer<typeof setMemberLocationsSchema>;

export const memberLocationAccessSchema = z.object({
  memberId: uuidSchema,
  scope: z.enum(LOCATION_SCOPES),
  /**
   * The locations they may touch. Empty when scope is `all` — the list is not
   * "every location enumerated", because that would go stale the moment a new
   * location was added.
   */
  branches: z.array(
    z.object({ branchId: uuidSchema, branchName: z.string(), type: z.enum(LOCATION_TYPES) }),
  ),
});
export type MemberLocationAccess = z.infer<typeof memberLocationAccessSchema>;
